import axios from 'axios'

const ENTRY_SESSION_KEY = 'games:entry:auth'
const ENTRY_PASSED_KEY = 'games:entry:passed'

export function getEntryDeniedUrl() {
    return `${window.location.origin}/404.html`
}

export function isEntryDeniedPage() {
    const path = window.location.pathname || ''
    return path === '/404.html' || path.endsWith('/404.html')
}

export function isDevHost() {
    const host = window.location.hostname
    return host === 'localhost' || host === '127.0.0.1'
}

function parseAuthFromSearch(search) {
    if (!search || search[0] !== '?') {
        return ''
    }

    const params = new URLSearchParams(search)
    const auth = params.get('auth')
    if (!auth || auth === 'null') {
        return ''
    }

    return auth
}

export function getUrlAuth() {
    const direct = parseAuthFromSearch(window.location.search)
    if (direct) {
        return direct
    }

    const hash = window.location.hash || ''
    const queryIndex = hash.indexOf('?')
    if (queryIndex >= 0) {
        return parseAuthFromSearch(hash.slice(queryIndex))
    }

    return ''
}

export function rememberEntryAuth(auth) {
    if (auth) {
        sessionStorage.setItem(ENTRY_SESSION_KEY, auth)
    }
}

export function getRememberedEntryAuth() {
    return sessionStorage.getItem(ENTRY_SESSION_KEY) || ''
}

export function resolveEntryAuth() {
    const auth = getUrlAuth() || getRememberedEntryAuth()
    if (auth) {
        rememberEntryAuth(auth)
    }
    return auth
}

/** 跨子域跳转时在 URL 上附带 auth（sessionStorage 不跨域共享） */
export function appendEntryAuthToUrl(url, auth) {
    if (!auth) {
        return url
    }

    const encoded = encodeURIComponent(auth)
    const hashIndex = url.indexOf('#')
    const base = hashIndex >= 0 ? url.slice(0, hashIndex) : url
    const joiner = base.indexOf('?') >= 0 ? '&' : '?'
    const withQuery = `${base}${joiner}auth=${encoded}`

    if (hashIndex >= 0) {
        return withQuery + url.slice(hashIndex)
    }

    return `${withQuery}#/login?auth=${encoded}`
}

export function mobileSubdomainToPc(subdomain) {
    if (!subdomain || subdomain.indexOf('wap') !== 0) {
        return subdomain || 'www'
    }
    const pc = subdomain.slice(3)
    return pc || 'www'
}

export function pcSubdomainToMobile(subdomain) {
    if (!subdomain) {
        return 'waplinea'
    }
    if (subdomain.indexOf('wap') === 0) {
        return subdomain
    }
    if (subdomain === 'www') {
        return 'waplinea'
    }
    return 'wap' + subdomain
}

export function markEntryPassed() {
    sessionStorage.setItem(ENTRY_PASSED_KEY, '1')
}

export function hasEntryPassed() {
    return sessionStorage.getItem(ENTRY_PASSED_KEY) === '1'
}

export function redirectEntryDenied() {
    window.location.replace(getEntryDeniedUrl())
    return true
}

export function redirectToSearch(searchUrl) {
    if (!searchUrl) {
        return false
    }
    window.location.replace(searchUrl)
    return true
}

export function applyEntryTips(vm, res) {
    if (!res || Number(res.status) !== 1) {
        return false
    }

    const data = res.data
    if (typeof data === 'string') {
        if (vm && vm.cooperation) {
            vm.cooperation.tips = data
        }
        return true
    }

    if (vm && vm.cooperation) {
        vm.cooperation.tips = data.content || ''
    }
    if (data && data.web_title) {
        document.title = data.web_title
    }

    return true
}

function normalizeApiBase(apiBase) {
    if (!apiBase) {
        return '/v3/'
    }
    return apiBase.endsWith('/') ? apiBase : `${apiBase}/`
}

/**
 * JSONP 校验入口（绕过前端子域 -> API 的 CORS 限制）
 */
function requestEntryTipsJsonp(apiBase, auth) {
    const base = normalizeApiBase(apiBase)
    const callback = `__entryTips${Date.now()}`
    const url = `${base}index/Cooperation/tips?auth=${encodeURIComponent(auth)}&callback=${callback}`

    return new Promise((resolve, reject) => {
        let timer = setTimeout(() => {
            cleanup()
            reject(new Error('timeout'))
        }, 20000)

        window[callback] = (data) => {
            cleanup()
            resolve(data)
        }

        const script = document.createElement('script')
        script.src = url
        script.onerror = () => {
            cleanup()
            reject(new Error('load failed'))
        }

        function cleanup() {
            clearTimeout(timer)
            try {
                delete window[callback]
            } catch (e) {}
            if (script.parentNode) {
                script.parentNode.removeChild(script)
            }
        }

        document.head.appendChild(script)
    })
}

/**
 * 入口校验专用请求，不走全局 ajax 拦截器
 */
export function requestEntryTips(apiBase, auth) {
    const base = normalizeApiBase(apiBase)
    const url = `${base}index/Cooperation/tips?auth=${encodeURIComponent(auth)}`

    return requestEntryTipsJsonp(apiBase, auth).catch(() => {
        return axios.get(url, {
            timeout: 20000,
            validateStatus: () => true,
        }).then(res => {
            if (res && res.status === 200 && res.data && typeof res.data === 'object') {
                return res.data
            }
            return null
        })
    })
}

export function verifyEntryGate(vm, dispatch, apiBase) {
    if (isDevHost() || isEntryDeniedPage()) {
        return Promise.resolve(true)
    }

    if (hasEntryPassed()) {
        return Promise.resolve(true)
    }

    const auth = resolveEntryAuth()
    if (!auth) {
        redirectEntryDenied()
        return Promise.resolve(false)
    }

    return requestEntryTips(apiBase, auth).then(res => {
        if (res && Number(res.status) === 1) {
            applyEntryTips(vm, res)
            markEntryPassed()
            rememberEntryAuth(auth)
            return true
        }

        redirectEntryDenied()
        return false
    }).catch(() => {
        redirectEntryDenied()
        return false
    })
}

export function loadLoginPageExtras(vm, dispatch) {
    const auth = getRememberedEntryAuth()
    const tasks = [
        dispatch('base/GET_LINE'),
    ]

    if (auth) {
        tasks.unshift(dispatch('base/TIPS_COOPERATION', { auth }).then(res => {
            applyEntryTips(vm, res)
            return res
        }).catch(() => null))
    }

    return Promise.all(tasks)
}
